import { useMemo } from "react";
import {
  useNodeTree,
  useNodeTreeState,
  Node,
} from "@darksnow-ui/node-tree-headless";
import { MenuItemConfig as NewMenuItemConfig } from "@darksnow-ui/menus";
import {
  FolderOpen,
  FolderClosed,
  FolderPlus,
  FilePlus,
  Copy,
  Scissors,
  Clipboard,
  Edit2,
  Trash2,
} from "lucide-react";

export interface NodeMenuContext {
  nodeId: string;
  node: Node | null;
  isRoot: boolean;
  isExpanded: boolean;
  controller: any;
  services: any;
}

interface UseNodeTreeMenuItemsOptions {
  onAction?: (action: string, nodeId: string) => void;
  customItems?:
    | NewMenuItemConfig<NodeMenuContext>[]
    | ((context: NodeMenuContext) => NewMenuItemConfig<NodeMenuContext>[]);
}

/**
 * Hook para converter os items do menu do node-tree para o formato do @darksnow-ui/menus
 */
export function useNodeTreeMenuItems(
  nodeId: string,
  options: UseNodeTreeMenuItemsOptions = {},
) {
  const { services, controller } = useNodeTree();
  const nodes = useNodeTreeState((state) => state.nodes);
  const expandedNodes = useNodeTreeState((state) => state.expandedNodes);
  const clipboardNodeIds = useNodeTreeState((state) => state.clipboardNodeIds);

  const isRoot = nodeId === "root" || !nodeId;
  const node = isRoot ? null : nodes.get(nodeId) || null;
  const isExpanded = !isRoot && expandedNodes.has(nodeId);
  const hasClipboard = clipboardNodeIds && clipboardNodeIds.size > 0;

  const context: NodeMenuContext = {
    nodeId,
    node,
    isRoot,
    isExpanded,
    controller,
    services,
  };

  const defaultItems = useMemo((): NewMenuItemConfig<NodeMenuContext>[] => {
    if (isRoot) {
      // Menu para área vazia (root)
      const items: NewMenuItemConfig<NodeMenuContext>[] = [
        {
          type: "item",
          label: "New Folder",
          icon: <FolderPlus className="h-4 w-4" />,
          handle: async (ctx) => {
            const name = prompt("New folder name:");
            if (name) {
              await ctx.controller.createNode(undefined, name);
              options.onAction?.("create-folder", ctx.nodeId);
            }
          },
        },
        {
          type: "item",
          label: "New File",
          icon: <FilePlus className="h-4 w-4" />,
          handle: async (ctx) => {
            const name = prompt("New file name:");
            if (name) {
              await ctx.controller.createNode(undefined, name);
              options.onAction?.("create-file", ctx.nodeId);
            }
          },
        },
      ];

      if (hasClipboard) {
        items.push(
          { type: "separator" },
          {
            type: "item",
            label: "Paste",
            icon: <Clipboard className="h-4 w-4" />,
            shortcut: "Ctrl+V",
            handle: async (ctx) => {
              await ctx.controller.pasteNodes(undefined);
              options.onAction?.("paste", ctx.nodeId);
            },
          },
        );
      }

      return items;
    }

    // Menu para nodes
    const items: NewMenuItemConfig<NodeMenuContext>[] = [];

    // Expand/Collapse para pastas
    if (node?.expandable) {
      items.push({
        type: "item",
        label: isExpanded ? "Collapse" : "Expand",
        icon: isExpanded ? (
          <FolderClosed className="h-4 w-4" />
        ) : (
          <FolderOpen className="h-4 w-4" />
        ),
        handle: (ctx) => {
          if (ctx.isExpanded) {
            ctx.services.navigationService.collapseNode(ctx.nodeId);
          } else {
            ctx.services.navigationService.expandNode(ctx.nodeId);
          }
          options.onAction?.(
            ctx.isExpanded ? "collapse" : "expand",
            ctx.nodeId,
          );
        },
      });
      items.push({ type: "separator" });
    }

    // New Folder/File para pastas
    if (node?.expandable) {
      items.push(
        {
          type: "item",
          label: "New Folder",
          icon: <FolderPlus className="h-4 w-4" />,
          handle: async (ctx) => {
            const name = prompt("New folder name:");
            if (name) {
              await ctx.controller.createNode(ctx.nodeId, name);
              options.onAction?.("create-folder", ctx.nodeId);
            }
          },
        },
        {
          type: "item",
          label: "New File",
          icon: <FilePlus className="h-4 w-4" />,
          handle: async (ctx) => {
            const name = prompt("New file name:");
            if (name) {
              await ctx.controller.createNode(ctx.nodeId, name);
              options.onAction?.("create-file", ctx.nodeId);
            }
          },
        },
        { type: "separator" },
      );
    }

    // Copy/Cut
    items.push(
      {
        type: "item",
        label: "Copy",
        icon: <Copy className="h-4 w-4" />,
        shortcut: "Ctrl+C",
        handle: (ctx) => {
          ctx.controller.copyNodes([ctx.nodeId]);
          options.onAction?.("copy", ctx.nodeId);
        },
      },
      {
        type: "item",
        label: "Cut",
        icon: <Scissors className="h-4 w-4" />,
        shortcut: "Ctrl+X",
        handle: (ctx) => {
          ctx.controller.cutNodes([ctx.nodeId]);
          options.onAction?.("cut", ctx.nodeId);
        },
      },
    );

    // Paste para pastas
    if (node?.expandable && hasClipboard) {
      items.push({
        type: "item",
        label: "Paste",
        icon: <Clipboard className="h-4 w-4" />,
        shortcut: "Ctrl+V",
        handle: async (ctx) => {
          await ctx.controller.pasteNodes(ctx.nodeId);
          options.onAction?.("paste", ctx.nodeId);
        },
      });
    }

    // Rename/Delete
    items.push(
      { type: "separator" },
      {
        type: "item",
        label: "Rename",
        icon: <Edit2 className="h-4 w-4" />,
        shortcut: "F2",
        handle: async (ctx) => {
          await ctx.controller.renameNode(ctx.nodeId);
          options.onAction?.("rename", ctx.nodeId);
        },
      },
      {
        type: "item",
        label: "Delete",
        icon: <Trash2 className="h-4 w-4" />,
        className: "text-destructive",
        shortcut: "Delete",
        handle: async (ctx) => {
          await ctx.controller.deleteNodes([ctx.nodeId]);
          options.onAction?.("delete", ctx.nodeId);
        },
      },
    );

    return items;
  }, [isRoot, node, isExpanded, hasClipboard, options.onAction]);

  // Mesclar com items customizados se fornecidos
  const finalItems = useMemo(() => {
    if (!options.customItems) {
      return defaultItems;
    }

    const customItems =
      typeof options.customItems === "function"
        ? options.customItems(context)
        : options.customItems;

    return [...defaultItems, ...customItems];
  }, [defaultItems, options.customItems, context]);

  return {
    items: finalItems,
    context,
  };
}
